import { useCallback, useEffect, useState } from 'react';
import { SlideBar } from '../../appSlideBar';
import { ContributorList } from '../../tableComponents/contributorTable';
import { useLocation } from 'react-router-dom';
import { Text, useDisclosure } from '@chakra-ui/react';
import { formatDate } from '../helpers/GoalTable.utils';
import SearchInputBar from '../../formComponents/SearchInputBar';
import { useDebounce } from '../../hooks/useDebounce';
import TeamTable from '../../TeamTable';
import { TeamMenu } from '../../teammenu';
import { ISelect } from '../../formComponents/customSelect';
import { getActivityFilters } from '../../shared/utils';
import useQueryState from '../../hooks/useQueryState';
import { useMetricSidebarStore } from '../../hooks/useMetricSidebarStore';

interface KeyResultViewDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  metricKey: string;
  metricTitle: string;
  goalStartDate?: string;
  goalEndDate?: string;
  scope?: string;
  keyResultTitle: string;
}

export const KeyResultViewDetails = ({
  isOpen,
  onClose,
  metricKey,
  metricTitle,
  goalStartDate,
  goalEndDate,
  scope,
  keyResultTitle,
}: KeyResultViewDetailsProps) => {
  const { pathname } = useLocation();
  const { closeSidebar } = useMetricSidebarStore();
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderColumn, setOrderColumn] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [teamId, setTeamId] = useQueryState('team');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const handleTeamSettingsModal = useDisclosure();
  const [selectedTeam, setSelectedTeam] = useState<ISelect>({
    value: '',
    label: 'Organisation',
  });

  const handleTeamSelection = (teamObj: ISelect) => {
    setSelectedTeam(teamObj);
  };

  const handleKeyResultDrawerClose = useCallback(() => {
    closeSidebar();
    onClose();
  }, [closeSidebar, onClose]);

  useEffect(() => {
    if (!isOpen) {
      closeSidebar();
    }
  }, [isOpen, closeSidebar]);

  return (
    <SlideBar
      openDrawer={isOpen}
      handleDrawerClose={handleKeyResultDrawerClose}
      sideBarHeading={keyResultTitle}
      isModalOpen={handleTeamSettingsModal.isOpen}
      sideBarSubHeading={
        <Text fontSize={'sm'} color={'text.secondary2'} mt={1}>
          Metric:{' '}
          <Text as={'span'} fontWeight={'medium'}>
            {metricTitle}
          </Text>
          , Duration:{' '}
          <Text as={'span'} fontWeight={'medium'}>
            {goalStartDate ? formatDate(goalStartDate) : '-'} -{' '}
            {goalEndDate ? formatDate(goalEndDate) : '-'}
          </Text>
        </Text>
      }
      width={scope === 'ORG' ? '762px' : '748px'}
      bodyHeight="92vh"
      SideDrawerBodyHeight={false}
      content={
        scope === 'TEAM' ? (
          <>
            <SearchInputBar
              searchValue={searchTerm}
              setSearchValue={setSearchTerm}
              placeholder="Search Contributor"
              size={'sm'}
              w={'fit-content'}
            />
            <ContributorList
              metricFilterState={[]}
              metricTooltips={[]}
              compareTo="pastPeriod"
              searchTerm={debouncedSearchTerm}
              teamState={
                teamId
                  ? [decodeURIComponent(teamId)]
                  : [decodeURIComponent(pathname.split('/')[2])]
              }
              repoState={[]}
              languageState={[]}
              jobRoleState={[]}
              activityFilter={getActivityFilters('active')}
              orderBy={orderBy}
              orderColumn={orderColumn}
              setOrderColumn={setOrderColumn}
              setOrderBy={setOrderBy}
              selectedMetrics={[
                {
                  metricTitle: metricTitle,
                  metricKey:
                    metricKey === 'PR_CYCLE_TIME_BREAKDOWN'
                      ? 'PR_CYCLE_TIME'
                      : metricKey,
                  sequence: 1,
                },
              ]}
              hiddenColumns={['Languages', 'Knowledge Area', 'Team Allocation']}
              height="76vh"
              startDate={goalStartDate}
              endDate={goalEndDate}
            />
          </>
        ) : (
          <>
            <TeamMenu
              selectedTeam={selectedTeam}
              handleSelectedTeam={handleTeamSelection}
              noDefault
              hideShowDetails
            />
            <TeamTable
              height="80vh"
              startDate={goalStartDate}
              endDate={goalEndDate}
              selectedMetrics={[
                {
                  metricTitle: metricTitle,
                  metricKey: metricKey,
                  sequence: 1,
                  id: metricKey === 'CUSTOM' ? metricTitle : metricKey,
                },
              ]}
              teamId={selectedTeam.value}
              handleTeamSettingsModal={handleTeamSettingsModal}
              orderBy={orderBy}
              orderColumn={orderColumn}
              setOrderColumn={setOrderColumn}
              setOrderBy={setOrderBy}
            />
          </>
        )
      }
    />
  );
};
